import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Pencil, X, Clock, Plus, ExternalLink, User, Link, Trash, Check, Radio, EyeOff, MapPin } from 'lucide-react';
import { newsService } from '../../../services';
import { devService } from '../../../services/dev.service';
import { useAuth } from '../../../context/AuthContext';
import { shortenEmail } from '../../../utils/email';
import Toast from '../../common/Toast/Toast';
import './NewsCard.css';
import { DEFAULT_LANGUAGE, NATIONAL_FEED_LANGUAGES, LANGUAGE_DEFAULT_STATE } from '@shared/languages.js';

// Category-wise dummy image URLs (using placeholder services with category-specific images)
const CATEGORY_IMAGES = {
  National: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322451199_757.jpg',
  Business: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322446926_903.jpg',
  Sports: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322447376_94.jpg',
  World: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322446584_697.jpg',
  Politics: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322451612_74.jpg',
  Technology: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322450829_392.jpg',
  Startup: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322452049_264.jpg',
  Entertainment: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322448662_37.jpg',
  Miscellaneous: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449929_664.jpg',
  Hatke: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322450829_392.jpg',
  Science: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322450268_226.jpg',
  Automobile: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449000_634.jpg',
  Education: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449426_933.jpg',
  Environment: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322445045_502.jpg',
  Travel: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322448299_383.jpg',
  Fashion: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322447765_483.jpg',
};

const DEFAULT_IMAGE = 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449929_664.jpg';

// A short's Inshorts hashId is written by a create_post call that the backend
// fires off after the generate response has already left, so a brand-new card
// arrives without one - and go-live refuses news that has no hash. The card
// asks for the hash once a second until it lands.
const HASH_POLL_INTERVAL_MS = 1000;
// Capped: a create that failed outright will never produce a hash, and a tab
// left open must not keep asking for one forever.
const HASH_POLL_MAX_ATTEMPTS = 60;
// Statuses a short can be in while its hash is still on the way - the same set
// the Go Live button is drawn for.
const HASH_POLL_STATUSES = ['draft', 'created', 'published', 'modified'];
// Past this age the create has long since answered or died, so an old hashless
// record is left alone rather than polled on every page load.
const HASH_POLL_MAX_AGE_MS = 15 * 60 * 1000;

const getNewsImage = (news) => {
  if (news.imageUrl) return news.imageUrl;
  // Use first category from categories array for image selection
  const firstCategory = news.categories && news.categories.length > 0 ? news.categories[0] : news.category;
  return CATEGORY_IMAGES[firstCategory] || DEFAULT_IMAGE;
};

const NewsCard = ({ news, onEnterEditMode, onClick, draggable, onDragStart, onDragEnd, onLongPress, isSelected = false, onCancelSelection, showAddToDeckButton = true, showEditShortsButton = false, showPublishButton = true, currentDeckId = null, onDeckSelect, onRemoveFromDeck, hideOverlayCancel = false, newsDecks, customImageButton, onGoLiveSuccess }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userLangs = user?.lang || [];
  const canEditNews = userLangs.length === 0 || userLangs.includes(news.metadata?.language || DEFAULT_LANGUAGE);
  const [isDragging, setIsDragging] = useState(false);
  const [showDecksModal, setShowDecksModal] = useState(false);
  const [decksList, setDecksList] = useState(newsDecks || []);
  const [fetchingDecks, setFetchingDecks] = useState(false);
  const [decksModalPosition, setDecksModalPosition] = useState({ x: 0, y: 0 });

  // Long-press state
  const longPressTimerRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const [isLongPressed, setIsLongPressed] = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0 });
  const cardRef = useRef(null);
  const badgeRef = useRef(null);

  // Confirm remove state
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);

  // Confirm go live state
  const [showConfirmPublish, setShowConfirmPublish] = useState(false);
  const [publishFeedType, setPublishFeedType] = useState('national'); // 'national' or 'regional'
  const [publishState, setPublishState] = useState(''); // For regional feed
  const [publishDistrict, setPublishDistrict] = useState(''); // For regional feed
  const [publishSubDistrict, setPublishSubDistrict] = useState(''); // For regional feed
  const [statesList, setStatesList] = useState([]); // States from backend
  // Feed location rules from backend - which languages may publish nationally
  // and which state each location-only language defaults to
  // Seeded from the shared language table so the form is correct before the
  // backend answers; /dev/states still overrides it, since that is the server's
  // live view of the same rules.
  const [feedRules, setFeedRules] = useState({
    nationalFeedLanguages: NATIONAL_FEED_LANGUAGES,
    languageDefaultState: LANGUAGE_DEFAULT_STATE,
  });
  const [districtSuggestions, setDistrictSuggestions] = useState([]); // District suggestions
  const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false); // Show district dropdown
  const [fetchDistrictsLoading, setFetchDistrictsLoading] = useState(false); // Loading state
  const [publishingNews, setPublishingNews] = useState(false); // Go Live state
  const [publishToast, setPublishToast] = useState(null); // Go Live error toast ({ message, type })
  // Hash found by polling, for a card that was handed news without one
  const [polledHashId, setPolledHashId] = useState(null);

  // Live news actions state (shown when the edit pencil on a live card is clicked)
  const [showLiveActions, setShowLiveActions] = useState(false); // Unpublish / Change Region chooser
  const [showConfirmUnpublish, setShowConfirmUnpublish] = useState(false); // Yes / No confirmation
  const [unpublishingNews, setUnpublishingNews] = useState(false); // Unpublish in-flight state

  // Location tooltip state
  const [showLocationTooltip, setShowLocationTooltip] = useState(false);

  // Outside English/Hindi the Inshorts feed is built only from
  // location-indexed news - a national publish never reaches a feed. Those
  // languages always publish regionally with a state, pre-selected to their
  // own state but changeable.
  const newsLanguage = news.metadata?.language || DEFAULT_LANGUAGE;
  const isStateMandatory = !feedRules.nationalFeedLanguages.includes(newsLanguage);
  const defaultStateCode = isStateMandatory ? (feedRules.languageDefaultState[newsLanguage] || '') : '';

  // The top-right corner of the image already belongs to the deck controls.
  // They are each absolutely positioned, so nothing keeps the hashtag badge
  // from landing underneath one - when a control is on screen the badge drops
  // to a second row instead. Vertical, because the deck badge's width varies.
  const hasTopRightControl =
    showAddToDeckButton ||
    (!!onRemoveFromDeck && (news.deckCount || 0) === 0) ||
    (news.deckCount || 0) > 0 ||
    decksList.length > 0;

  // Long-press handlers for mobile
  const handleTouchStart = (e) => {
    if (!cardRef.current) return;

    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };

    longPressTimerRef.current = setTimeout(() => {
      setIsLongPressed(true);
      // Get card position
      const rect = cardRef.current.getBoundingClientRect();
      // Position menu at the center top of card
      const menuWidth = 160; // min-width from CSS
      setContextMenu({
        show: true,
        x: rect.left + rect.width / 2 - menuWidth / 2,
        y: rect.top
      });
      // Vibrate on long press (if supported)
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500);
  };

  const handleTouchMove = (e) => {
    if (!longPressTimerRef.current) return;

    const touch = e.touches[0];
    const moveX = Math.abs(touch.clientX - touchStartRef.current.x);
    const moveY = Math.abs(touch.clientY - touchStartRef.current.y);

    // Cancel long-press if moved more than 10px (user is scrolling)
    if (moveX > 10 || moveY > 10) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Close context menu
  const closeContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0 });
    setIsLongPressed(false);
  };

  // Handle menu item clicks
  const handleAddToDeck = () => {
    if (onLongPress) {
      onLongPress(news);
    }
    closeContextMenu();
  };

  const handleOpenInNewTab = () => {
    window.open(`/dashboard/news/${news._id}`, '_blank');
    closeContextMenu();
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenu.show && cardRef.current && !cardRef.current.contains(e.target)) {
        closeContextMenu();
      }
    };

    if (contextMenu.show) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [contextMenu.show]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // Sync decksList when newsDecks prop changes
  useEffect(() => {
    if (newsDecks && newsDecks.length > 0) {
      setDecksList(newsDecks);
    }
  }, [newsDecks]);

  // Fetch states from backend for regional feed
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await devService.getStates();
        if (response.success) {
          setStatesList(response.data.states || []);
          setFeedRules((prev) => ({
            nationalFeedLanguages: response.data.nationalFeedLanguages || prev.nationalFeedLanguages,
            languageDefaultState: response.data.languageDefaultState || prev.languageDefaultState,
          }));
        }
      } catch (err) {
        console.error('Failed to fetch states:', err);
        setStatesList([]);
      }
    };
    fetchStates();
  }, []);

  // Fetch districts when state is selected or district search changes
  useEffect(() => {
    const fetchDistricts = async () => {
      // Only fetch if state is selected and we're in regional mode
      if (!publishState || publishFeedType !== 'regional') {
        setDistrictSuggestions([]);
        setShowDistrictSuggestions(false);
        return;
      }

      // Don't fetch if district text is too short
      if (publishDistrict.length < 2) {
        setDistrictSuggestions([]);
        setShowDistrictSuggestions(false);
        return;
      }

      try {
        setFetchDistrictsLoading(true);
        const response = await devService.getDistricts(publishState, publishDistrict);
        if (response.success) {
          const districts = response.data.districts || [];
          setDistrictSuggestions(districts);
          setShowDistrictSuggestions(districts.length > 0);
        }
      } catch (err) {
        console.error('Failed to fetch districts:', err);
        setDistrictSuggestions([]);
        setShowDistrictSuggestions(false);
      } finally {
        setFetchDistrictsLoading(false);
      }
    };

    // Debounce the district search
    const timeoutId = setTimeout(() => {
      fetchDistricts();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [publishState, publishDistrict, publishFeedType]);

  // Apply the stored location (or the language default) to the publish form
  const applyPublishDefaults = () => {
    const { locationLevel, state, district, tehsil } = news.location || {};
    // location.state holds the full state name when it came from AI extraction
    // and the 2-letter code once the news has been published, so normalise it
    // before comparing against the dropdown values
    const storedStateCode = getStateCode(state) || '';
    const publishedRegionally = ['STATE', 'DISTRICT', 'SUB_DISTRICT'].includes(locationLevel);

    if (isStateMandatory) {
      // Always regional with a state pre-selected: keep the region it is
      // already live on, else fall back to the language's own state
      const nextState = (publishedRegionally && storedStateCode) || defaultStateCode;
      setPublishFeedType('regional');
      setPublishState(nextState);
      // Only carry over the finer location when it sits inside that state -
      // otherwise it would build an invalid state/district pair
      const keepSubLocation = Boolean(nextState) && nextState === storedStateCode;
      setPublishDistrict(keepSubLocation ? (district || '') : '');
      setPublishSubDistrict(keepSubLocation ? (tehsil || '') : '');
      return;
    }

    if (locationLevel === 'ALL') {
      setPublishFeedType('national');
      setPublishState('');
      setPublishDistrict('');
      setPublishSubDistrict('');
    } else if (publishedRegionally) {
      setPublishFeedType('regional');
      setPublishState(storedStateCode);
      setPublishDistrict(district || '');
      setPublishSubDistrict(tehsil || '');
    } else {
      // Not published yet - start on national with nothing pre-filled
      setPublishFeedType('national');
      setPublishState('');
      setPublishDistrict('');
      setPublishSubDistrict('');
    }
  };

  // Initialize publish form when the confirm dialog opens
  useEffect(() => {
    if (!showConfirmPublish) return;
    applyPublishDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfirmPublish, news.location, isStateMandatory, defaultStateCode]);

  // Helper function to reset form based on existing location
  const resetPublishForm = () => {
    applyPublishDefaults();
  };

  // Update dropdown position on scroll when modal is open
  useEffect(() => {
    if (!showDecksModal || !badgeRef.current) return;

    const updatePosition = () => {
      const rect = badgeRef.current?.getBoundingClientRect();
      if (!rect) return;
      const windowWidth = window.innerWidth;
      setDecksModalPosition({
        x: windowWidth - rect.right,
        y: rect.bottom + 8
      });
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showDecksModal]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDecksModal) return;

    const handleClickOutside = (e) => {
      // Don't close if clicking on the badge button
      if (badgeRef.current && badgeRef.current.contains(e.target)) {
        return;
      }
      // Don't close if clicking inside the dropdown
      const dropdown = document.querySelector('.decks-dropdown');
      if (dropdown && dropdown.contains(e.target)) {
        return;
      }
      setShowDecksModal(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showDecksModal]);

  const handleCardClick = (e) => {
    // Check if modifier keys are pressed (Ctrl, Cmd, Shift, Alt)
    const isModifierPressed = e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;

    if (onClick) {
      onClick(e);
    } else if (isModifierPressed) {
      // Open in new tab if modifier key is pressed
      window.open(`/dashboard/news/${news._id}`, '_blank');
    } else {
      // Open in current window (using navigate for SPA)
      navigate(`/dashboard/news/${news._id}`);
    }
  };

  // Open the detail page with the editor already open - the same screen the
  // Edit button on the detail page gives you (?edit=true is what it reads)
  const handleOpenEditPage = (e) => {
    const editUrl = `/dashboard/news/${news._id}?edit=true`;
    const isModifierPressed = e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;

    if (isModifierPressed) {
      window.open(editUrl, '_blank');
    } else {
      navigate(editUrl);
    }
  };

  // Handle right-click to show context menu (desktop)
  const handleContextMenu = (e) => {
    e.preventDefault();
    setIsLongPressed(true);
    // For desktop right-click, position at mouse cursor
    // Account for the translateX(-50%) transform
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY + 10
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const currentYear = new Date().getFullYear();

    // If same year, show dd/mm hh:mm
    // If different year, show dd/mm/yy
    if (date.getFullYear() === currentYear) {
      return format(date, 'dd/MM • HH:mm');
    } else {
      return format(date, 'dd/MM/yy');
    }
  };

  // State name to code mapping
  const getStateCode = (stateName) => {
    if (!stateName) return null;

    // If stateName is already a code (2-3 uppercase letters), return it directly.
    // TG is the old Telangana code - Inshorts uses TS, so map anything stored
    // before that change onto the code the dropdown now carries.
    if (/^[A-Z]{2,3}$/.test(stateName)) {
      return stateName === 'TG' ? 'TS' : stateName;
    }

    const stateCodeMap = {
      'Andhra Pradesh': 'AP',
      'Arunachal Pradesh': 'AR',
      'Assam': 'AS',
      'Bihar': 'BR',
      'Chhattisgarh': 'CT',
      'Goa': 'GA',
      'Gujarat': 'GJ',
      'Haryana': 'HR',
      'Himachal Pradesh': 'HP',
      'Jharkhand': 'JH',
      'Karnataka': 'KA',
      'Kerala': 'KL',
      'Madhya Pradesh': 'MP',
      'Maharashtra': 'MH',
      'Manipur': 'MN',
      'Meghalaya': 'ML',
      'Mizoram': 'MZ',
      'Nagaland': 'NL',
      'Odisha': 'OD',
      'Punjab': 'PB',
      'Rajasthan': 'RJ',
      'Sikkim': 'SK',
      'Tamil Nadu': 'TN',
      'Telangana': 'TS', // Inshorts' code for Telangana, not the ISO TG
      'Tripura': 'TR',
      'Uttar Pradesh': 'UP',
      'Uttarakhand': 'UK',
      'West Bengal': 'WB',
      'Delhi': 'DL',
      'Jammu & Kashmir': 'JK',
      'Ladakh': 'LA',
      'Puducherry': 'PY',
      'Chandigarh': 'CH',
      'Dadra & Nagar Haveli and Daman & Diu': 'DN',
      'Andaman & Nicobar Islands': 'AN',
      'Lakshadweep': 'LD',
    };

    return stateCodeMap[stateName] || null;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'h:mm a');
  };

  const formatGenerationTime = (ms) => {
    if (!ms || ms === 0) return '';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatStatus = (status) => {
    const statusMap = {
      draft: 'Draft',
      created: 'Created',
      processing: 'Processing',
      published: 'Published',
      archived: 'Archived',
      rejected: 'Rejected',
      modified: 'Modified',
    };
    return statusMap[status?.toLowerCase()] || status || 'Draft';
  };

  const countWords = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const countChars = (text) => {
    if (!text) return 0;
    return text.length;
  };

  // Check if hashId looks like a MongoDB ObjectId (24 hex characters)
  const isMongoObjectId = (id) => {
    if (!id) return false;
    return /^[0-9a-fA-F]{24}$/.test(id);
  };

  // The hash this card publishes with: whatever it was handed, or whatever the
  // poll below found afterwards.
  const hashId = news.hashId || polledHashId;

  // Going live needs a real Inshorts hash. A missing one means the create call
  // has not come back yet (a freshly generated short), an ObjectId means it
  // came back without a hash - neither can be published until it syncs.
  const needsInshortsSync = !hashId || isMongoObjectId(hashId);

  // Only a short that could still be given a hash is worth asking about. The
  // create never re-runs, so without the age and status guards every old
  // hashless record on screen would poll for a hash that is not coming.
  const isAwaitingHash =
    showPublishButton &&
    !hashId &&
    !news.isLive &&
    HASH_POLL_STATUSES.includes(news.status) &&
    !!news.createdAt &&
    Date.now() - new Date(news.createdAt).getTime() < HASH_POLL_MAX_AGE_MS;

  useEffect(() => {
    if (!isAwaitingHash) return;

    let cancelled = false;
    let timer = null;
    let attempts = 0;

    // Self-scheduling rather than setInterval, so a slow response delays the
    // next ask instead of stacking requests on top of it
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;

      try {
        const response = await newsService.getNewsHashId(news._id);
        if (cancelled) return;

        const result = response?.data;
        if (result?.hashId) {
          // Enables Go Live on this render
          setPolledHashId(result.hashId);
          return;
        }
        // Nothing is ever going to write a hash on this record
        if (result && result.pending === false) return;
      } catch (err) {
        // A hash that has not arrived yet comes back as a plain success, so a
        // failed request is a real error - give up rather than hammer it
        console.error('Failed to fetch hashId for news', news._id, err);
        return;
      }

      if (attempts >= HASH_POLL_MAX_ATTEMPTS) return;
      timer = setTimeout(poll, HASH_POLL_INTERVAL_MS);
    };

    timer = setTimeout(poll, HASH_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isAwaitingHash, news._id]);

  // Truncate text to specified character limit
  const truncateText = (text, maxLength = 100) => {
    if (!text || text.length === 0) return '';

    // Count words
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    const totalWords = words.reduce((sum, word) => sum + word.length, 0);

    // Calculate how many words fit within the limit
    // Each word roughly equals a character with space, so 100 chars = ~100 words
    let displayText = '';
    let displayedWordCount = 0;

    for (const word of words) {
      const wordWithSpace = word + ' ';
      // If adding this word would exceed the character limit
      if ((displayedWordCount + 1) * word.length > maxLength) {
        // Add ellipsis for words that fit
        displayText += (displayedWordCount > 0 ? ' ' : '') + word;
        displayedWordCount++;
      } else if (displayedWordCount < maxLength &&
                (displayedWordCount + 1) * (word.length + 1) <= maxLength) {
        // Add the word if it fits
        displayText += (displayedWordCount > 0 ? ' ' : '') + word;
        displayedWordCount++;
      }
      // If we've reached max, stop and add ellipsis
      if (displayText.length >= maxLength) {
        displayText += '...';
        break;
      }
    }

    return displayText;
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    if (onDragStart) {
      onDragStart(e);
    }
    // Set drag effect
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', news._id);
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
    if (onDragEnd) {
      onDragEnd(e);
    }
  };

  return (
    <>
      <div
        ref={cardRef}
        className={`inshorts-card inshorts-card--${(news.status || 'draft').toLowerCase()} ${isDragging ? 'dragging' : ''} ${isLongPressed ? 'inshorts-card--long-pressed' : ''} ${isSelected ? 'inshorts-card--selected' : ''}`}
        onClick={handleCardClick}
        onContextMenu={handleContextMenu}
        draggable={draggable}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
      {getNewsImage(news) && (
        <div className="inshorts-card-image">
          <img src={getNewsImage(news)} alt={news.headline} loading="lazy" />

          {/* Dark overlay for text readability */}
          <div className="image-overlay"></div>

          {/* Hashtag - top right over the image. The editor attaches one, so
              there is normally a single badge; older shorts can carry more and
              those are counted rather than wrapped across the image. */}
          {news.hashtags?.length > 0 && (
            <div
              className={`image-hashtag-badge${hasTopRightControl ? ' image-hashtag-badge--stacked' : ''}`}
              title={news.hashtags.map((h) => h.title || h.label).join(', ')}
            >
              {/* The label, not the title - it is what actually ships to
                  Inshorts, and it reads as a hashtag. Title is in the tooltip. */}
              <span className="image-hashtag-badge-label">#{news.hashtags[0].label}</span>
              {news.hashtags.length > 1 && (
                <span className="image-hashtag-badge-more">+{news.hashtags.length - 1}</span>
              )}
            </div>
          )}

          {/* Status Badge - top right - HIDDEN */}
          {/*news.status && (
            <div className={`image-status-badge image-status-badge--${news.status.toLowerCase()}`}>
              {formatStatus(news.status)}
            </div>
          )}*/}

          {/* Remove from Deck button - top right (shown when in deck context and no decks badge) */}
          {onRemoveFromDeck && (news.deckCount || 0) === 0 && !showConfirmRemove && (
            <button
              className="card-remove-from-deck-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowConfirmRemove(true);
              }}
              title="Remove from deck"
            >
              <Trash size={14} />
            </button>
          )}

          {/* Confirm remove buttons (shown when no decks badge) */}
          {onRemoveFromDeck && (news.deckCount || 0) === 0 && showConfirmRemove && (
            <div className="card-confirm-remove">
              <span className="card-confirm-remove-text">Remove from deck?</span>
              <button
                className="card-confirm-remove-yes"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFromDeck(news._id);
                  setShowConfirmRemove(false);
                }}
              >
                Yes
              </button>
              <button
                className="card-confirm-remove-no"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowConfirmRemove(false);
                }}
              >
                No
              </button>
            </div>
          )}

          {/* Add to Deck button - top right (shown conditionally) */}
          {showAddToDeckButton && (
            <button
              className="card-add-to-deck-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleAddToDeck();
              }}
              title="Add to Deck"
            >
              📚
            </button>
          )}

          {/* Categories as simple text overlay */}
          {(news.categories && news.categories.length > 0) ? (
            <span className="image-categories-text">
              {news.categories.join(' • ')}
            </span>
          ) : (
            <span className="image-categories-text">{news.category}</span>
          )}

          {/* Deck Count Badge - top right of image */}
          {(news.deckCount > 0 || decksList.length > 0) && (
            <div className="image-deck-count-badge-container">
              <button
                ref={badgeRef}
                className="image-deck-count-badge"
                onClick={async (e) => {
                  e.stopPropagation();
                  // Toggle dropdown: close if already open
                  if (showDecksModal) {
                    setShowDecksModal(false);
                    return;
                  }
                  // Position dropdown below the button (must calculate before async)
                  const rect = e.currentTarget?.getBoundingClientRect();
                  if (!rect) return;
                  const windowWidth = window.innerWidth;
                  const dropdownWidth = 220;
                  // Fetch decks if not already loaded
                  if (decksList.length === 0 && news.deckCount > 0 && !fetchingDecks) {
                    setFetchingDecks(true);
                    try {
                      const response = await newsService.getNewsDecks(news._id);
                      setDecksList(response.data?.decks || response.data || []);
                    } catch (err) {
                      console.error('Failed to fetch decks:', err);
                      setDecksList([]);
                    } finally {
                      setFetchingDecks(false);
                    }
                  }
                  setDecksModalPosition({
                    x: windowWidth - rect.right, // Distance from right edge
                    y: rect.bottom + 8
                  });
                  setShowDecksModal(true);
                }}
                title={`View all ${news.deckCount > 0 ? news.deckCount : decksList.length} decks`}
              >
                <span>
                  {' '}
                  {news.deckCount > 0 ? news.deckCount : decksList.length}{' '}
                  {(news.deckCount > 0 ? news.deckCount : decksList.length) === 1 ? '📘' : '📚'}
                </span>
              </button>
              {/* Delete button on badge */}
              {onRemoveFromDeck && !showConfirmRemove && (
                <button
                  className="badge-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowConfirmRemove(true);
                  }}
                  title="Remove from deck"
                >
                  <Trash size={12} />
                </button>
              )}
              {/* Confirm remove buttons on badge */}
              {onRemoveFromDeck && showConfirmRemove && (
                <div className="badge-confirm-remove">
                  <button
                    className="badge-confirm-yes"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFromDeck(news._id);
                      setShowConfirmRemove(false);
                    }}
                  >
                    <Check size={12} />
                  </button>
                  <button
                    className="badge-confirm-no"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowConfirmRemove(false);
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Selected message overlay */}
          {isSelected && (
            <div className="card-selected-overlay">
              <span className="selected-message">📚 Selected. Tap a deck to add.</span>
              {!hideOverlayCancel && (
                <button
                  className="selected-cancel-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onCancelSelection) onCancelSelection();
                  }}
                  title="Cancel selection"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          )}

          {/* Edit Shorts Button - bottom right of image, on every card.
              Pages that host the editor themselves (news detail, deck detail)
              hand over onEnterEditMode; everywhere else the button opens the
              detail page already in edit mode, which is that same editor.
              Stacked above a custom bottom-right action when one is present. */}
          {canEditNews && !isSelected && (
            <button
              className={`card-edit-shorts-btn${customImageButton ? ' card-edit-shorts-btn--stacked' : ''}`}
              onClick={(e) => {
                e.stopPropagation();

                if (showEditShortsButton && onEnterEditMode) {
                  onEnterEditMode(news._id);
                  return;
                }

                handleOpenEditPage(e);
              }}
              title="Edit Short"
            >
              <Pencil size={12} />
              <span>Edit Short</span>
            </button>
          )}

          {/* Go Live Button - shown for every publishable status, including the
              'draft' a short carries the moment it is generated; it sits
              disabled until the Inshorts hash arrives rather than being hidden,
              so a just-generated card never looks like it cannot go live */}
          {showPublishButton && canEditNews && (news.status === 'draft' || news.status === 'created' || news.status === 'published' || news.status === 'modified') && !showConfirmPublish && !showLiveActions && !showConfirmUnpublish && !news.isLive && (
            <button
              className={`card-publish-deck-btn ${needsInshortsSync ? 'card-publish-deck-btn--disabled' : ''}`}
              title={needsInshortsSync ? "Syncing required" : "Go Live"}
              onClick={(e) => {
                e.stopPropagation();
                // Without a usable hash the button is disabled - no confirm
                if (!needsInshortsSync) {
                  setShowConfirmPublish(true);
                }
              }}
              disabled={needsInshortsSync}
            >
              Go Live
            </button>
          )}

          {/* Live Badge - shown when news.isLive is true; edit pencil only if the user has language permission */}
          {showPublishButton && news.isLive && !showConfirmPublish && !showLiveActions && !showConfirmUnpublish && (
            <button
              className="card-live-badge-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (!canEditNews) return;
                // Live news: let the user pick between unpublishing and
                // changing the region before opening the publish form
                setShowLiveActions(true);
              }}
              title={canEditNews ? 'Edit Live News' : 'Live'}
            >
              <div className="live-indicator-dot"></div>
              {canEditNews && <Pencil size={14} />}
            </button>
          )}

          {/* Unpublished Badge - shown once a live news has been taken down */}
          {showPublishButton && !news.isLive && news.unpublishedAt && !showConfirmPublish && !showLiveActions && !showConfirmUnpublish && (
            <div
              className="card-unpublished-badge"
              title={`Unpublished at ${formatDateTime(news.unpublishedAt)}${news.unpublishedBy ? ` by ${shortenEmail(news.unpublishedBy)}` : ''}`}
            >
              <EyeOff size={12} />
              <span>Unpublished • {formatDateTime(news.unpublishedAt)}</span>
            </div>
          )}

          {/* Custom Image Button - for deck detail page Add button */}
          {customImageButton}
        </div>
      )}

      <div className="inshorts-card-content">
        <h3 className="inshorts-card-headline">
          {news.headline}
        </h3>

        {news.summary && (
          <p className="inshorts-card-summary">
            {news.summary}
          </p>
        )}
      </div>

      {/* Bottom Border with metadata */}
      <div className="inshorts-card-footer">
        <div className="footer-top">
          <div className="footer-left">
            <span className="footer-time">{formatDateTime(news.createdAt)}</span>
            <span className="footer-separator">•</span>
            {news.generationTime && news.generationTime > 0 ? (
              <span className="footer-generation-time" title="Generation Time">
                <Clock size={12} />
                <span>{formatGenerationTime(news.generationTime)}</span>
              </span>
            ) : (
              <span className="footer-generation-time footer-generation-time--empty" title="Generation Time">
                <Clock size={12} />
                <span>--</span>
              </span>
            )}
          </div>
          <div className="footer-right">
            <span className="footer-word-count">
              {countWords(news.headline)}w/{countChars(news.headline)}c hl • {countWords(news.summary)}w/{countChars(news.summary)}c smry
            </span>
          </div>
        </div>
        <div className={`footer-bottom ${showLocationTooltip && news.location?.state ? 'footer-bottom--location' : ''}`}>
          <span className={`footer-user ${showLocationTooltip && news.location?.state ? 'footer-hidden' : ''}`} title="Updated by">
            <User size={12} />
            <span>{shortenEmail(news.updatedBy || news.userId?.email || 'Unknown')}</span>
          </span>
          {news.location?.state || news.location?.locationLevel === 'ALL' ? (
            <span
              className="footer-state"
              title={news.location.state || 'India'}
              onMouseEnter={() => setShowLocationTooltip(true)}
              onMouseLeave={() => setShowLocationTooltip(false)}
              onClick={(e) => {
                e.stopPropagation();
                setShowLocationTooltip(!showLocationTooltip);
              }}
            >
              {showLocationTooltip ? (
                <span className="footer-state-full">
                  {news.location.locationLevel === 'ALL' ? 'India' : news.location.state}
                  {news.location.district && ` • ${news.location.district}`}
                  {news.location.tehsil && ` • ${news.location.tehsil}`}
                  {news.location.village && ` • ${news.location.village}`}
                </span>
              ) : (
                <span>{news.location.locationLevel === 'ALL' ? 'IN' : getStateCode(news.location.state)}</span>
              )}
            </span>
          ) : null}
          {news.sourceUrl ? (
            <a
              href={news.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`footer-source ${showLocationTooltip && news.location?.state ? 'footer-hidden' : ''}`}
              title="Open source"
              onClick={(e) => e.stopPropagation()}
            >
              <Link size={12} />
              <span>Source</span>
            </a>
          ) : (
            <span className={`footer-source footer-source--empty ${showLocationTooltip && news.location?.state ? 'footer-hidden' : ''}`} title="No source available">
              <Link size={12} />
              <span>No Source</span>
            </span>
          )}
        </div>
      </div>

      {/* Live News Actions Overlay - choose between unpublishing and changing the region */}
      {showPublishButton && showLiveActions && (
        <div className="card-confirm-publish-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="card-confirm-publish-content card-live-actions-content" onClick={(e) => e.stopPropagation()}>
            <h4 className="card-confirm-publish-title">This news is live</h4>
            <div className="card-live-actions-options">
              <button
                className="card-live-action-btn card-live-action-btn--unpublish"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLiveActions(false);
                  setShowConfirmUnpublish(true);
                }}
              >
                <EyeOff size={16} />
                <span>Unpublish</span>
              </button>
              <button
                className="card-live-action-btn card-live-action-btn--region"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLiveActions(false);
                  setShowConfirmPublish(true);
                }}
              >
                <MapPin size={16} />
                <span>Change Region</span>
              </button>
            </div>
            <div className="card-confirm-publish-actions">
              <button
                className="card-confirm-publish-decline"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLiveActions(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Unpublish Overlay - Yes / No */}
      {showPublishButton && showConfirmUnpublish && (
        <div className="card-confirm-publish-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="card-confirm-publish-content card-confirm-unpublish-content" onClick={(e) => e.stopPropagation()}>
            <h4 className="card-confirm-publish-title">Unpublish this news?</h4>
            <p className="card-confirm-unpublish-text">
              It will be removed from the feed. You can go live again anytime.
            </p>
            <div className="card-confirm-publish-actions">
              <button
                className="card-confirm-publish-decline"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowConfirmUnpublish(false);
                }}
                disabled={unpublishingNews}
              >
                No
              </button>
              <button
                className="card-confirm-unpublish-yes"
                onClick={async (e) => {
                  e.stopPropagation();
                  setUnpublishingNews(true);
                  setPublishToast(null);

                  try {
                    const response = await newsService.unpublishNews(news._id);

                    if (response.data) {
                      setShowConfirmUnpublish(false);
                      setPublishToast({
                        message: 'News unpublished successfully',
                        type: 'success',
                      });
                      // Reuse the go-live callback so the list refreshes
                      if (onGoLiveSuccess) {
                        onGoLiveSuccess(response.data);
                      }
                    }
                  } catch (err) {
                    console.error('Failed to unpublish:', err);
                    setPublishToast({
                      message: err.response?.data?.message || 'Failed to unpublish. Please try again.',
                      type: 'error',
                    });
                  } finally {
                    setUnpublishingNews(false);
                  }
                }}
                disabled={unpublishingNews}
              >
                {unpublishingNews ? 'Unpublishing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Go Live Overlay - covers entire card. Statuses must match the
          Go Live button's, or a draft opens a confirm that never renders */}
      {showPublishButton && (news.isLive || news.status === 'draft' || news.status === 'created' || news.status === 'published' || news.status === 'modified') && showConfirmPublish && (
        <div className="card-confirm-publish-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="card-confirm-publish-content" onClick={(e) => e.stopPropagation()}>
            {/* Feed Type Selection - National is not offered for languages
                whose Inshorts feed is built only from location-indexed news */}
            <div className="card-confirm-publish-feed-type">
              {!isStateMandatory && (
                <button
                  className={`card-confirm-publish-feed-btn ${publishFeedType === 'national' ? 'active' : ''}`}
                  onClick={(e) => setPublishFeedType('national')}
                >
                  National Feed
                </button>
              )}
              <button
                className={`card-confirm-publish-feed-btn ${publishFeedType === 'regional' ? 'active' : ''}`}
                onClick={(e) => setPublishFeedType('regional')}
                disabled={isStateMandatory}
              >
                Regional Feed
              </button>
            </div>

            {/* National Feed - India selected by default */}
            {publishFeedType === 'national' && (
              <div className="card-confirm-publish-national">
                <span className="card-confirm-publish-selected-country">India</span>
              </div>
            )}

            {/* Regional Feed - State, District, Sub-district */}
            {publishFeedType === 'regional' && (
              <div className="card-confirm-publish-regional">
                <div className="card-confirm-publish-field">
                  <label>State *</label>
                  <select
                    value={publishState}
                    onChange={(e) => setPublishState(e.target.value)}
                    className="card-confirm-publish-select"
                  >
                    {/* These languages need a state to show up in the feed at
                        all, so the blank choice is only there as a fallback
                        when no state could be pre-selected */}
                    {(!isStateMandatory || !publishState) && <option value="">Select State</option>}
                    {statesList.map((state) => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="card-confirm-publish-field" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={publishDistrict}
                    onChange={(e) => setPublishDistrict(e.target.value)}
                    onFocus={() => {
                      if (districtSuggestions.length > 0) {
                        setShowDistrictSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay hiding to allow clicking on suggestions
                      setTimeout(() => setShowDistrictSuggestions(false), 200);
                    }}
                    placeholder="Enter district"
                    className="card-confirm-publish-input"
                    autoComplete="off"
                  />
                  {fetchDistrictsLoading && (
                    <div className="card-district-loading">
                      <span style={{ fontSize: '12px', color: '#666' }}>Loading...</span>
                    </div>
                  )}
                  {showDistrictSuggestions && districtSuggestions.length > 0 && (
                    <div className="card-district-suggestions">
                      {districtSuggestions.map((district, index) => (
                        <div
                          key={index}
                          className="card-district-suggestion-item"
                          onMouseDown={(e) => {
                            e.preventDefault(); // Prevent onBlur from firing
                            setPublishDistrict(district);
                            setShowDistrictSuggestions(false);
                          }}
                        >
                          {district}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="card-confirm-publish-field">
                  <input
                    type="text"
                    value={publishSubDistrict}
                    onChange={(e) => setPublishSubDistrict(e.target.value)}
                    placeholder="Enter sub district"
                    className="card-confirm-publish-input"
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="card-confirm-publish-actions">
              <button
                className="card-confirm-publish-decline"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowConfirmPublish(false);
                  // Reset form to location defaults
                  resetPublishForm();
                }}
              >
                Decline
              </button>
              <button
                className="card-confirm-publish-golive"
                onClick={async (e) => {
                  e.stopPropagation();

                  // These languages can never go out nationally - the editor
                  // picks the state, we only force the feed type
                  const feedToSend = isStateMandatory ? 'regional' : publishFeedType;
                  const stateToSend = publishState;

                  // Validation for regional feed
                  if (feedToSend === 'regional' && !stateToSend) {
                    alert('Please select a state');
                    return;
                  }

                  setPublishingNews(true);
                  setPublishToast(null);

                  try {
                    const response = await newsService.goLiveNews(news._id, {
                      feed: feedToSend,
                      state: feedToSend === 'regional' ? stateToSend : undefined,
                      district: feedToSend === 'regional' ? publishDistrict : undefined,
                      subDistrict: feedToSend === 'regional' ? publishSubDistrict : undefined,
                    });

                    if (response.data) {
                      setShowConfirmPublish(false);
                      // Reset form to location defaults
                      resetPublishForm();
                      // Call success callback if provided
                      if (onGoLiveSuccess) {
                        onGoLiveSuccess(response.data);
                      }
                    }
                  } catch (err) {
                    console.error('Failed to go live:', err);
                    setPublishToast({
                      message: err.response?.data?.message || 'Failed to go live. Please try again.',
                      type: 'error',
                    });
                  } finally {
                    setPublishingNews(false);
                  }
                }}
                disabled={publishingNews}
              >
                {publishingNews ? 'Going Live...' : 'Confirm Go Live'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu (shows on long-press) */}
      {contextMenu.show && (
        <div
          className="card-context-menu"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`
          }}
        >
          <div className="context-menu-item" onClick={handleAddToDeck}>
            <Plus size={16} />
            <span>Add to Deck</span>
          </div>
          <div className="context-menu-item" onClick={handleOpenInNewTab}>
            <ExternalLink size={16} />
            <span>Open in New Tab</span>
          </div>
        </div>
      )}
    </div>

    {/* Decks Dropdown - rendered via Portal to avoid z-index issues */}
    {showDecksModal && createPortal(
        <div
          className="decks-dropdown"
          style={{
            right: `${decksModalPosition.x}px`,
            top: `${decksModalPosition.y}px`
          }}
          onClick={(e) => e.stopPropagation()}>
          <div className="decks-dropdown-list">
            {fetchingDecks ? (
              <div className="decks-dropdown-loading">Loading decks...</div>
            ) : decksList.length === 0 ? (
              <div className="decks-dropdown-empty">No decks found</div>
            ) :
              decksList.map((deck) => (
              <div
                key={deck._id}
                className={`decks-dropdown-item ${deck._id === currentDeckId ? 'decks-dropdown-item--current' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDeckSelect) {
                    onDeckSelect(deck._id);
                  } else {
                    navigate(`/dashboard/decks/${deck._id}`, { state: { selectedNewsId: news._id } });
                  }
                  setShowDecksModal(false);
                }}
              >
                <span className="decks-dropdown-icon">📘</span>
                <div className="decks-dropdown-info">
                  <span className="decks-dropdown-name">{deck.name}</span>
                  <span className={`decks-dropdown-status decks-dropdown-status--${(deck.status || 'draft').toLowerCase()}`}>
                    {deck.status ? deck.status.toUpperCase() : 'DRAFT'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}

    {publishToast && createPortal(
      <Toast
        message={publishToast.message}
        type={publishToast.type}
        onClose={() => setPublishToast(null)}
      />,
      document.body
    )}
    </>
  );
};

export default NewsCard;
